import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { Alert, Button, Checkbox } from '@ntconsult/aluma-react';
import { isAxiosError } from 'axios';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { useResponder } from '../hooks';
import { CartaoPasso } from './CartaoPasso';
import type { CtxPasso } from './passo.types';
import { ResumoResposta, resumoDosValores } from './ResumoResposta';

const erroCaixa = 'text-sm text-danger';

/** A tela final: o endereço em destaque, a previsão, o tamanho, o cadastro e as caixas (R12 a R15). */
export function PassoConfirmar({
  ctx,
  responder,
}: {
  ctx: CtxPasso;
  responder: ReturnType<typeof useResponder>;
}) {
  const { t } = useTranslation(['formulario', 'erros']);
  const { form, participacao, atual, cadastro, paises, estados, cidades } = ctx;
  const v = form.watch();
  const e = form.formState.errors;
  const quer = v.deseja === 'sim';
  const resumo = resumoDosValores(v, atual, cadastro, {
    uf: (id) => estados.data?.find((x) => String(x.id) === id)?.sigla ?? '',
    cidade: (id) => ctx.rotulosCidade[id] ?? '',
    pais: (id) => paises.data?.find((x) => String(x.id) === id)?.nome ?? '',
  });
  // Sem as listas do Axiis, o caminho que as usa não pode ser enviado (como antes da spec 33).
  const listasFora = v.oficialMarcado && (paises.isError || estados.isError || cidades.isError);
  const paisesFora = quer && v.origem === 'exterior' && paises.isError;
  // 409 é tratado pela página (aviso próprio); aqui ficam os demais erros do envio.
  const erroEnvio =
    responder.error && !(isAxiosError(responder.error) && responder.error.response?.status === 409)
      ? responder.error
      : null;

  const acaoCadastro = v.oficialMarcado ? (
    <Button type="button" variant="ghost" size="sm" onClick={ctx.corrigirCadastro}>
      {t('formulario:confirmar.alterar')}
    </Button>
  ) : quer && v.mesmo === 'nao' ? (
    <Button type="button" variant="secondary" size="sm" onClick={ctx.corrigirCadastro}>
      {t('formulario:confirmar.corrigirCadastro')}
    </Button>
  ) : (
    <Button type="button" variant="ghost" size="sm" onClick={ctx.corrigirCadastro}>
      {t('formulario:confirmar.alterar')}
    </Button>
  );

  return (
    <CartaoPasso
      titulo={t('formulario:confirmar.titulo')}
      onVoltar={ctx.onVoltar}
      acao={
        <Button
          type="submit"
          variant="primary"
          loading={responder.isPending}
          disabled={responder.isPending || listasFora || paisesFora}
        >
          {participacao.resposta ? t('formulario:salvarAlteracoes') : t('formulario:enviar')}
        </Button>
      }
    >
      {erroEnvio && (
        <div role="alert">
          <Alert variant="danger" title={t(`erros:${mensagemDeErro(erroEnvio)}`)} />
        </div>
      )}
      <ResumoResposta
        resumo={resumo}
        campanha={participacao.campanha}
        acaoCadastro={acaoCadastro}
      />
      {quer && (
        <div className="flex flex-col gap-3 border-t border-border pt-4">
          <p>{t('formulario:confirmar.intro')}</p>
          <Controller
            control={form.control}
            name="confirmaEndereco"
            render={({ field }) => (
              <Checkbox
                ref={field.ref}
                checked={field.value}
                onCheckedChange={(x) => {
                  field.onChange(x);
                  ctx.revalidar('confirmaEndereco');
                }}
                error={!!e.confirmaEndereco}
                label={t('formulario:confirmar.enderecoCorreto')}
              />
            )}
          />
          {e.confirmaEndereco && (
            <p role="alert" className={erroCaixa}>
              {e.confirmaEndereco.message}
            </p>
          )}
          <Controller
            control={form.control}
            name="confirmaRecebedor"
            render={({ field }) => (
              <Checkbox
                ref={field.ref}
                checked={field.value}
                onCheckedChange={(x) => {
                  field.onChange(x);
                  ctx.revalidar('confirmaRecebedor');
                }}
                error={!!e.confirmaRecebedor}
                label={t('formulario:confirmar.recebedor')}
              />
            )}
          />
          {e.confirmaRecebedor && (
            <p role="alert" className={erroCaixa}>
              {e.confirmaRecebedor.message}
            </p>
          )}
        </div>
      )}
    </CartaoPasso>
  );
}
